import type { QueryLanguage, Token, TokenType } from './types';

const SPL_KEYWORDS = new Set([
  'search', 'where', 'stats', 'eval', 'table', 'sort', 'head', 'tail',
  'dedup', 'rename', 'rex', 'lookup', 'join', 'spath', 'timechart',
  'tstats', 'top', 'rare', 'transaction', 'eventstats', 'streamstats',
  'fields', 'fillnull', 'makemv', 'mvexpand', 'append', 'appendpipe',
  'index', 'sourcetype', 'host', 'source', 'earliest', 'latest',
  'by', 'as', 'over', 'span', 'limit', 'count', 'values', 'dc',
  'sum', 'avg', 'min', 'max', 'list', 'first', 'last', 'and', 'or', 'not',
  'true', 'false', 'null', 'in',
]);

const SPL_FUNCTIONS = new Set([
  'count', 'values', 'dc', 'sum', 'avg', 'min', 'max', 'list',
  'first', 'last', 'earliest', 'latest', 'perc', 'percentile',
  'stdev', 'var', 'range', 'median', 'mode', 'upper', 'lower',
  'len', 'substr', 'replace', 'split', 'mvcount', 'mvindex',
  'mvfilter', 'mvjoin', 'tonumber', 'tostring', 'strftime',
  'strptime', 'now', 'relative_time', 'if', 'case', 'coalesce',
  'cidrmatch', 'isbool', 'isint', 'isnum', 'isstr', 'isnull',
  'isnotnull', 'like', 'match', 'urldecode',
]);

const KQL_KEYWORDS = new Set([
  'where', 'project', 'summarize', 'extend', 'join', 'let', 'order',
  'by', 'top', 'distinct', 'union', 'parse', 'mv-expand', 'render',
  'take', 'limit', 'count', 'sort', 'asc', 'desc', 'on', 'kind',
  'has', 'contains', 'startswith', 'endswith', 'matches', 'regex',
  'and', 'or', 'not', 'in', 'between', 'ago', 'now', 'true', 'false',
  'null', 'dynamic', 'datatable', 'print', 'invoke', 'evaluate',
  'make-series', 'mv-apply', 'externaldata', 'search', 'find',
]);

const KQL_FUNCTIONS = new Set([
  'count', 'sum', 'avg', 'min', 'max', 'dcount', 'countif', 'sumif',
  'avgif', 'minif', 'maxif', 'percentile', 'variance', 'stdev',
  'make_list', 'make_set', 'arg_max', 'arg_min', 'any',
  'strcat', 'strlen', 'substring', 'tolower', 'toupper', 'trim',
  'replace', 'split', 'parse_json', 'tostring', 'toint', 'tolong',
  'todecimal', 'todatetime', 'totimespan', 'format_datetime',
  'datetime_diff', 'bin', 'floor', 'ceiling', 'round', 'iif', 'iff',
  'case', 'coalesce', 'isempty', 'isnotempty', 'isnull', 'isnotnull',
  'pack', 'pack_all', 'extract', 'extract_all', 'ipv4_is_match',
  'geo_point_to_s2cell', 'hash', 'base64_encode_tostring',
]);

const SQL_KEYWORDS = new Set([
  'select', 'from', 'where', 'join', 'inner', 'left', 'right', 'outer',
  'full', 'cross', 'on', 'group', 'by', 'order', 'having', 'insert',
  'into', 'update', 'set', 'delete', 'create', 'alter', 'drop',
  'table', 'index', 'view', 'union', 'all', 'distinct', 'limit',
  'offset', 'as', 'and', 'or', 'not', 'in', 'between', 'like',
  'is', 'null', 'exists', 'case', 'when', 'then', 'else', 'end',
  'asc', 'desc', 'true', 'false', 'with', 'recursive', 'values',
  'primary', 'key', 'foreign', 'references', 'constraint', 'default',
  'top', 'fetch', 'next', 'rows', 'only',
]);

const SQL_FUNCTIONS = new Set([
  'count', 'sum', 'avg', 'min', 'max', 'coalesce', 'nullif',
  'cast', 'convert', 'isnull', 'ifnull', 'nvl', 'upper', 'lower',
  'trim', 'ltrim', 'rtrim', 'substring', 'replace', 'concat',
  'length', 'len', 'charindex', 'patindex', 'left', 'right',
  'getdate', 'now', 'dateadd', 'datediff', 'datepart', 'year',
  'month', 'day', 'hour', 'minute', 'second', 'round', 'floor',
  'ceiling', 'abs', 'power', 'sqrt', 'log', 'exp', 'row_number',
  'rank', 'dense_rank', 'ntile', 'lead', 'lag', 'first_value',
  'last_value', 'over', 'partition',
]);

interface TokenizeConfig {
  pattern: RegExp;
  classify: (match: RegExpExecArray) => TokenType;
}

function classifySplMatch(m: RegExpExecArray): TokenType {
  if (m[1]) return 'operator';    // pipe
  if (m[2] || m[3]) return 'string';
  if (m[4]) return 'number';
  if (m[5]) {
    const name = m[5].trim().replace(/\s*$/, '').toLowerCase();
    return SPL_FUNCTIONS.has(name) ? 'function' : 'text';
  }
  if (m[6]) return SPL_KEYWORDS.has(m[6].toLowerCase()) ? 'keyword' : 'text';
  if (m[7]) return 'comparison';
  return 'text';
}

function classifyKqlMatch(m: RegExpExecArray): TokenType {
  if (m[1]) return 'operator';
  if (m[2] || m[3]) return 'string';
  if (m[4]) return 'number';
  if (m[5]) {
    const name = m[5].trim().replace(/\s*$/, '').toLowerCase();
    return KQL_FUNCTIONS.has(name) ? 'function' : 'text';
  }
  if (m[6]) return KQL_KEYWORDS.has(m[6].toLowerCase()) ? 'keyword' : 'text';
  if (m[7]) return 'comparison';
  return 'text';
}

function classifySqlMatch(m: RegExpExecArray): TokenType {
  if (m[1] || m[2]) return 'string';
  if (m[3]) return 'number';
  if (m[4]) {
    const name = m[4].trim().replace(/\s*$/, '').toLowerCase();
    if (SQL_FUNCTIONS.has(name)) return 'function';
    if (SQL_KEYWORDS.has(name)) return 'keyword';
    return 'text';
  }
  if (m[5]) return SQL_KEYWORDS.has(m[5].toLowerCase()) ? 'keyword' : 'text';
  if (m[6]) return 'comparison';
  return 'text';
}

function classifyLuceneMatch(m: RegExpExecArray): TokenType {
  if (m[1]) return 'string';
  if (m[2]) return 'function'; // field name
  if (m[3]) return 'operator'; // colon
  if (m[4]) return 'keyword';  // AND/OR/NOT/TO
  if (m[5]) return 'number';
  if (m[6]) return 'operator'; // special chars
  return 'text';
}

const CONFIGS: Record<QueryLanguage, TokenizeConfig> = {
  spl: {
    pattern: /(\|)|("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?(?:s|m|h|d)?\b)|((?:[a-zA-Z_][\w.]*)\s*(?=\())|(\b[a-zA-Z_][\w.]*\b)|(!=|>=|<=|==|=|>|<)/g,
    classify: classifySplMatch,
  },
  kql: {
    pattern: /(\|)|("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?(?:ms|s|m|h|d|tick)?\b)|((?:[a-zA-Z_][\w]*)\s*(?=\())|(\b[a-zA-Z_][\w]*\b)|(!=|!~|=~|>=|<=|==|=|>|<)/g,
    classify: classifyKqlMatch,
  },
  sql: {
    pattern: /("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?\b)|((?:[a-zA-Z_][\w]*)\s*(?=\())|(\b[a-zA-Z_][\w]*\b)|(!=|>=|<=|<>|=|>|<)/g,
    classify: classifySqlMatch,
  },
  lucene: {
    pattern: /("(?:[^"\\]|\\.)*")|([\w.]+(?=\s*:))|(:\s*)|(AND|OR|NOT|TO)|\b(\d+(?:\.\d+)?)\b|([*?~^])/g,
    classify: classifyLuceneMatch,
  },
};

/**
 * Tokenize a query string into platform-agnostic tokens.
 * Each token has a type (keyword, function, string, number, operator, comparison, text)
 * and a value (the matched text).
 *
 * This is platform-agnostic: web can render tokens as HTML spans,
 * React Native can render as <Text> components with styles.
 */
export function tokenizeQuery(code: string, language: QueryLanguage): Token[] {
  const config = CONFIGS[language];
  if (!config) return [{ type: 'text', value: code }];

  const tokens: Token[] = [];
  const pattern = new RegExp(config.pattern.source, config.pattern.flags);
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(code)) !== null) {
    // Add any text between the last match and this one
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', value: code.slice(lastIndex, match.index) });
    }

    const tokenType = config.classify(match);
    tokens.push({ type: tokenType, value: match[0] });
    lastIndex = match.index + match[0].length;
  }

  // Add trailing text
  if (lastIndex < code.length) {
    tokens.push({ type: 'text', value: code.slice(lastIndex) });
  }

  return tokens;
}
